import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { Editor } from '@tiptap/react';

import type { FormatType } from './types';

const HARD_BREAK_REGEX = /\[hardBreak]/g;
const DOUBLE_SPACE_LINE_BREAK_REGEX = / {2,}\n/g;

export const convertHardBreakPlaceholders = (value: string): string =>
  value.replace(HARD_BREAK_REGEX, (match, offset, original) => {
    if (offset > 0 && original[offset - 1] === '\\') {
      return match;
    }

    let lineStart = original.lastIndexOf('\n', offset - 1) + 1;

    if (lineStart < 0) {
      lineStart = 0;
    }

    while (lineStart < original.length && original[lineStart] === ' ') {
      lineStart += 1;
    }

    const isTableRow = original[lineStart] === '|';

    return isTableRow ? '<br>' : '  \n';
  });

export const formatCommands: Partial<Record<FormatType, (editor: Editor) => void>> = {
  bold: (editor) => editor.chain().focus().toggleBold().run(),
  italic: (editor) => editor.chain().focus().toggleItalic().run(),
  underline: (editor) => editor.chain().focus().toggleUnderline().run(),
  strike: (editor) => editor.chain().focus().toggleStrike().run(),
  highlight: (editor) => editor.chain().focus().toggleHighlight().run(),
  heading1: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
  heading2: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
  heading3: (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
  heading4: (editor) => editor.chain().focus().toggleHeading({ level: 4 }).run(),
  heading5: (editor) => editor.chain().focus().toggleHeading({ level: 5 }).run(),
  heading6: (editor) => editor.chain().focus().toggleHeading({ level: 6 }).run(),
  bulletList: (editor) => editor.chain().focus().toggleBulletList().run(),
  orderedList: (editor) => editor.chain().focus().toggleOrderedList().run(),
  blockquote: (editor) => editor.chain().focus().toggleBlockquote().run(),
  codeBlock: (editor) => editor.chain().focus().toggleCodeBlock().run(),
  table: (editor) => {
    const chain = editor.chain().focus();

    if (editor.isActive('table')) {
      chain.deleteTable().run();

      return;
    }

    chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  },
  tableAddRowBefore: (editor) => editor.chain().focus().addRowBefore().run(),
  tableAddRowAfter: (editor) => editor.chain().focus().addRowAfter().run(),
  tableDeleteRow: (editor) => editor.chain().focus().deleteRow().run(),
  tableAddColumnBefore: (editor) => editor.chain().focus().addColumnBefore().run(),
  tableAddColumnAfter: (editor) => editor.chain().focus().addColumnAfter().run(),
  tableDeleteColumn: (editor) => editor.chain().focus().deleteColumn().run(),
};

const escapeTableCell = (value: string): string => {
  const normalized = value
    .replace(/\r\n/g, '\n')
    .replace(HARD_BREAK_REGEX, '\n')
    .replace(DOUBLE_SPACE_LINE_BREAK_REGEX, '\n');
  const segments = normalized
    .split(/\n+/)
    .map((segment) => segment.replace(/\s+/g, ' ').trim())
    .filter((segment) => segment.length > 0);

  if (segments.length === 0) {
    return ' ';
  }

  return segments.map((segment) => segment.replace(/\|/g, '\\|')).join('<br>');
};

export const tableNodeToMarkdown = (node: ProseMirrorNode): string => {
  const rows: string[][] = [];
  let maxColumns = 0;

  node.forEach((row) => {
    const cells: string[] = [];

    row.forEach((cell) => {
      cells.push(escapeTableCell(cell.textContent ?? ''));
    });

    maxColumns = Math.max(maxColumns, cells.length);
    rows.push(cells);
  });

  if (rows.length === 0 || maxColumns === 0) {
    return '';
  }

  const normalizedRows = rows.map((cells) => {
    const result = [...cells];

    while (result.length < maxColumns) {
      result.push(' ');
    }

    return result;
  });

  const [header, ...body] = normalizedRows;
  const headerLine = `| ${header.join(' | ')} |`;
  const separatorLine = `| ${new Array(maxColumns).fill('---').join(' | ')} |`;
  const bodyLines = body.map((cells) => `| ${cells.join(' | ')} |`);

  return [headerLine, separatorLine, ...bodyLines].join('\n');
};

const TABLE_HTML_REGEXP = /<table[\s\S]*?<\/table>/;

export const buildServerMarkdown = (editor: Editor): string => {
  let md = editor.storage.markdown.getMarkdown();
  const tableMarkdownChunks: string[] = [];

  editor.state.doc.descendants((node) => {
    if (node.type.name === 'table') {
      tableMarkdownChunks.push(tableNodeToMarkdown(node));
    }
  });

  tableMarkdownChunks.forEach((chunk) => {
    if (md.includes('[table]')) {
      md = md.replace('[table]', chunk);
    } else {
      md = md.replace(TABLE_HTML_REGEXP, chunk);
    }
  });

  return convertHardBreakPlaceholders(md);
};
